import axios from 'axios';
import { API_URL } from 'env';

export const API_V1_BE_URL = API_URL + '/api/v1/be';

export const getAuthToken = (): string | null => {
  return localStorage.getItem('token');
};

export const apiClient = axios.create({
  baseURL: API_V1_BE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

apiClient.interceptors.request.use((config) => {
  const token = getAuthToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// ==========================================
// Types: Dashboard API
// ==========================================

export interface OverviewCardMetric {
  value: number | string;
  formatted?: string;
  change_text: string;
}

export interface OverviewCards {
  total_capital_managers: OverviewCardMetric;
  challenging: OverviewCardMetric;
  funded: OverviewCardMetric;
  has_payout: OverviewCardMetric;
  total_funded_capital: OverviewCardMetric;
  total_verified_payout: OverviewCardMetric;
  consistency_rate: OverviewCardMetric;
}

export interface FunnelStepItem {
  count: number;
  percent: number;
  label: string;
}

export interface JourneyFunnel {
  developing: FunnelStepItem;
  challenge: FunnelStepItem;
  verification: FunnelStepItem;
  funded: FunnelStepItem;
  payout_achieved: FunnelStepItem;
  consistent: FunnelStepItem;
}

export interface NetworkStatusCategory {
  count: number;
  percent: number;
}

export interface NetworkStatus {
  normal: NetworkStatusCategory;
  attention: NetworkStatusCategory;
  paused: NetworkStatusCategory;
  scale_ready: NetworkStatusCategory;
  total_cm: number;
}

export interface CapitalUnderManagementData {
  total_funded_capital: number;
  account_count: number;
  avg_account_size: number;
  tiers: Record<string, number>;
}

export interface PerformancePayoutItem {
  month_key: string;
  label: string;
  verified_payout: number;
  cm_payout_count: number;
}

export interface PerformanceFundedItem {
  month_key: string;
  label: string;
  new_funded_cm: number;
  total_funded_cm: number;
}

export interface PerformanceNewCmItem {
  month_key: string;
  label: string;
  new_capital_managers: number;
  growth_percent: number;
}

export interface PerformanceOverTimeData {
  payout: PerformancePayoutItem[];
  funded_cm: PerformanceFundedItem[];
  new_cm: PerformanceNewCmItem[];
}

export interface KeyIndicatorItem {
  value: string | number;
  trend: string;
}

export interface KeyIndicatorsData {
  verified_payout: KeyIndicatorItem;
  active_funded_acc: KeyIndicatorItem;
  consistency_score: KeyIndicatorItem;
  average_drawdown: KeyIndicatorItem;
  system_compliance: KeyIndicatorItem;
  risk_compliance: KeyIndicatorItem;
}

export interface DashboardData {
  overview_cards: OverviewCards;
  journey_funnel: JourneyFunnel;
  network_status: NetworkStatus;
  capital_under_management: CapitalUnderManagementData;
  performance_over_time: PerformanceOverTimeData;
  key_indicators: KeyIndicatorsData;
}

export interface DashboardApiResponse {
  success: boolean;
  message?: string;
  data: DashboardData;
}

// ==========================================
// Types: Top Capital Managers API
// ==========================================

export interface TopManagerItem {
  rank: number;
  id: number;
  account_number: string;
  platform: string;
  level: string;
  capital: number;
  capital_formatted: string;
  payout: boolean;
  discipline: number;
  risk: number;
  consistency: number;
  lumir_score: number;
  trend: 'up' | 'down' | 'stable' | string;
}

export interface CapitalManagersMeta {
  total_cms: number;
  current_page: number;
  per_page: number;
  last_page: number;
}

export interface CapitalManagersResponse {
  success: boolean;
  data: TopManagerItem[];
  meta: CapitalManagersMeta;
}

export interface CapitalManagersParams {
  search?: string;
  page?: number;
  per_page?: number;
  capital?: number | string;
}

// ==========================================
// Types: Capital Managers Detail (Stats Drilldown)
// ==========================================

export type StatDetailType =
  | 'total_cms'
  | 'challenge'
  | 'funded'
  | 'payout'
  | 'total_funded_capital'
  | 'active_consistent'
  | 'verification'
  | 'payout_achieved'
  | 'consistent'
  | 'developing';

export interface DetailAccountsParams {
  type: StatDetailType | string;
  search?: string;
  sort_by?: 'initial_balance' | 'balance' | string;
  order?: 'desc' | 'asc';
  page?: number;
  per_page?: number;
}

export interface DetailAccountItem {
  id: number;
  account_name: string;
  account_meta: string;
  platform: string;
  stage: string;
  capital: number;
  capital_text: string;
  balance: number;
  balance_text: string;
  pnl: number;
  pnl_text: string;
  pnl_color: string;
  dd: string;
  status: string;
  status_color: string;
}

export interface DetailAccountsMeta {
  total_accounts: number;
  current_page: number;
  per_page: number;
  last_page: number;
}

export interface DetailAccountsResponse {
  success: boolean;
  data: DetailAccountItem[];
  meta: DetailAccountsMeta;
}

// ==========================================
// API Methods
// ==========================================

/**
 * 1. GET /capital-managers/dashboard
 * Fetches dashboard overview metrics, journey funnel, network status,
 * capital under management, performance over time, and key indicators.
 */
export const fetchCapitalManagersDashboard = async (): Promise<DashboardData | null> => {
  try {
    const response = await apiClient.get<DashboardApiResponse>('/capital-managers/dashboard');
    if (response.data && response.data.data) {
      return response.data.data;
    }
    const raw = response.data as any;
    if (raw && (raw.capital_under_management || raw.overview_cards || raw.capitalUnderManagement || raw.journey_funnel || raw.journeyFunnel)) {
      return raw;
    }
    return null;
  } catch (error) {
    console.warn('API /capital-managers/dashboard failed:', error);
    return null;
  }
};

/**
 * 2. GET /capital-managers
 * Fetches top capital managers list with rankings, discipline, risk, consistency, lumir score.
 *
 * Query Params:
 * - search?: string
 * - page?: number (default: 1)
 * - per_page?: number (default: 10)
 */
export const fetchCapitalManagers = async (
  params?: CapitalManagersParams
): Promise<CapitalManagersResponse> => {
  try {
    const queryParams: CapitalManagersParams = {
      page: params?.page ?? 1,
      per_page: params?.per_page ?? 10,
      ...params,
    };
    const response = await apiClient.get<CapitalManagersResponse>('/capital-managers', {
      params: queryParams,
    });
    if (response.data && response.data.data) {
      const res = response.data;
      const data = (res.data || []).map((item: any, idx: number) => ({
        ...item,
        cm_id: item.cm_id ?? item.account_name ?? item.name ?? (item.id ? `CM #${item.id}` : `CM-${idx + 1}`),
        level: item.level ?? 'CM-I',
        capital_formatted: item.capital_formatted ?? (item.capital ? `$${Number(item.capital).toLocaleString()}` : '$0'),
      }));
      const rawMeta = res.meta || (res as any);
      const totalCms = Number(rawMeta?.total_cms ?? (res as any).total ?? data.length);
      const currentPage = Number(rawMeta?.current_page ?? queryParams.page ?? 1);
      const perPage = Number(rawMeta?.per_page ?? queryParams.per_page ?? 10);
      const lastPage = Number(rawMeta?.last_page ?? Math.ceil(totalCms / perPage) ?? 1);

      return {
        ...res,
        data,
        meta: {
          total_cms: totalCms,
          current_page: currentPage,
          per_page: perPage,
          last_page: lastPage,
        },
      };
    }
    return {
      success: false,
      data: [],
      meta: {
        total_cms: 0,
        current_page: queryParams.page || 1,
        per_page: queryParams.per_page || 10,
        last_page: 1,
      },
    };
  } catch (error) {
    console.warn('API /capital-managers failed:', error);
    return {
      success: false,
      data: [],
      meta: {
        total_cms: 0,
        current_page: params?.page ?? 1,
        per_page: params?.per_page ?? 10,
        last_page: 1,
      },
    };
  }
};

/**
 * 3. GET /capital-managers/detail?type=
 * Fetches accounts list drill-down for the stat overview cards and journey funnel stages.
 *
 * Query Params:
 * - type: total_cms | challenge | funded | payout | total_funded_capital | active_consistent | verification | payout_achieved | consistent | developing
 * - search: Search in list by Name, Account Number, or Broker
 * - sort_by: initial_balance | balance (default: initial_balance)
 * - order: desc | asc (default: desc)
 * - page: int
 * - per_page: int
 */
export const fetchCapitalManagersDetail = async (
  params: DetailAccountsParams
): Promise<DetailAccountsResponse> => {
  try {
    const response = await apiClient.get<DetailAccountsResponse>('/capital-managers/detail', {
      params,
    });
    if (response.data && response.data.data) {
      return response.data;
    }
    return {
      success: false,
      data: [],
      meta: {
        total_accounts: 0,
        current_page: params?.page || 1,
        per_page: params?.per_page || 10,
        last_page: 1,
      },
    };
  } catch (error) {
    console.warn('API /capital-managers/detail failed:', error);
    return {
      success: false,
      data: [],
      meta: {
        total_accounts: 0,
        current_page: params?.page || 1,
        per_page: params?.per_page || 10,
        last_page: 1,
      },
    };
  }
};

/**
 * 4. GET /trade-history/reports?account_number=...&date_to=...
 * Fetches account trade history report based on account number and end date.
 */
export const fetchAccountTradeReport = async (
  accountNumber: string,
  dateTo?: string
): Promise<any> => {
  try {
    const today = dateTo || new Date().toISOString().slice(0, 10);
    const response = await apiClient.get('/trade-history/reports', {
      params: {
        account_number: accountNumber,
        date_to: today,
      },
    });
    if (response.data && response.data.data) {
      return response.data.data;
    }
    return response.data;
  } catch (error) {
    console.warn(`API /trade-history/reports failed for account ${accountNumber}:`, error);
    return null;
  }
};

